// src/features/booking/bookingSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/requests';
import log from '../../logger'; // Import the logger

// Fetch bookings with pagination & filters
export const fetchBookings = createAsyncThunk(
  'booking/fetchBookings',
  async ({ page = 1, limit = 10, filters = {} }, { rejectWithValue }) => {
    try {
      log.info('Fetching bookings with page:', page, 'and limit:', limit); // Log the fetch action
      const response = await api.bookings.getAll({ page, limit, filters });
      if (response.data.status) {
        log.info('Bookings fetched successfully:', response.data.data); // Log successful fetch
        return response.data.data; // Adjust based on your API response structure
      } else {
        log.warn('Failed to fetch bookings:', response.data.message); // Log warning on failure
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching bookings:', error); // Log the error
      return rejectWithValue(error.response?.data?.message || 'Error fetching bookings');
    }
  }
);

// Fetch business booking counts
export const fetchBusinessBookingCounts = createAsyncThunk(
  'booking/fetchBusinessBookingCounts',
  async (_, { rejectWithValue }) => {
    try {
      log.info('Fetching business booking counts'); // Log the fetch action
      const response = await api.bookings.getBusinessCounts();
      if (response.data.status) {
        log.info('Business booking counts fetched successfully:', response.data.data); // Log successful fetch
        return response.data.data; // Return the business counts data
      } else {
        log.warn('Failed to fetch business booking counts:', response.data.message); // Log warning on failure
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching business booking counts:', error); // Log the error
      return rejectWithValue(error.response?.data?.message || 'Error fetching business booking counts');
    }
  }
);

// Create booking
export const createBooking = createAsyncThunk(
  'booking/createBooking',
  async (formData, { rejectWithValue }) => {
    try {
      log.info('Creating booking with data:', formData); // Log the creation action
      const response = await api.bookings.create(formData); // Use the create method
      if (response.data.status) {
        log.info('Booking created successfully:', response.data.data); // Log successful creation
        return response.data.data; // Return the created booking
      } else {
        log.warn('Failed to create booking:', response.data.message); // Log warning on failure
        return rejectWithValue(response.data.message); // Return error message
      }
    } catch (error) {
      log.error('Error creating booking:', error); // Log the error
      return rejectWithValue(error.response?.data?.message || 'Failed to create booking');
    }
  }
);

// Update booking
export const updateBooking = createAsyncThunk(
  'booking/updateBooking',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      log.info('Updating booking with ID:', id, 'and data:', formData); // Log the update action
      const response = await api.bookings.update(id, formData); // Use the update method
      if (response.data.status) {
        log.info('Booking updated successfully:', response.data.data); // Log successful update
        return response.data.data; // Return the updated booking
      } else {
        log.warn('Failed to update booking:', response.data.message); // Log warning on failure
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error updating booking:', error); // Log the error
      return rejectWithValue(error.response?.data?.message || 'Error updating booking');
    }
  }
);

// Delete booking
export const deleteBooking = createAsyncThunk(
  'booking/deleteBooking',
  async (id, { rejectWithValue }) => {
    try {
      log.info('Deleting booking with ID:', id); // Log the delete action
      const response = await api.bookings.delete(id); // Use the delete method
      if (response.data.status) {
        log.info('Booking deleted successfully:', id); // Log successful deletion
        return id; // return the id of the deleted booking
      } else {
        log.warn('Failed to delete booking:', response.data.message); // Log warning on failure
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error deleting booking:', error); // Log the error
      return rejectWithValue(error.response?.data?.message || 'Error deleting booking');
    }
  }
);

// Bulk delete bookings
export const bulkDeleteBookings = createAsyncThunk(
  'booking/bulkDeleteBookings',
  async (ids, { rejectWithValue }) => {
    try {
      await Promise.all(ids.map(id => api.bookings.delete(id)));
      return ids;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error deleting bookings');
    }
  }
);

// Get booking by ID
export const getBookingById = createAsyncThunk(
  'booking/getBookingById',
  async (id, { rejectWithValue }) => {
    try {
      log.info('Fetching booking with ID:', id); // Log the fetch action
      const response = await api.bookings.getById(id); // Use the getById method
      if (response.data.status) {
        log.info('Booking fetched successfully:', response.data.data); // Log successful fetch
        return response.data.data; // Return the booking data
      } else {
        log.warn('Failed to fetch booking:', response.data.message); // Log warning on failure
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching booking:', error); // Log the error
      return rejectWithValue(error.response?.data?.message || 'Error fetching booking');
    }
  }
);

const bookingSlice = createSlice({
  name: 'booking',
  initialState: {
    items: [],
    total: 0,
    page: 1,
    pages: 0,
    limit: 10,
    loading: false,
    error: null,
    booking: null,
    businessCounts: [],
    businessCountsLoading: false,
    businessCountsError: null,
  },
  reducers: {
    resetCurrentBooking: (state) => {
      state.booking = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBookings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBookings.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const { bookings, total, page, pages, limit } = action.payload;
        state.items = bookings;
        state.total = total;
        state.page = page;
        state.pages = pages;
        state.limit = limit;
      })
      .addCase(fetchBookings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createBooking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBooking.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items.push(action.payload);
      })
      .addCase(createBooking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateBooking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBooking.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = action.payload; // Update the existing booking
        }
      })
      .addCase(updateBooking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteBooking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBooking.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = state.items.filter(item => item._id !== action.payload); // Remove the deleted booking
      })
      .addCase(deleteBooking.rejected, (state) => {
        state.loading = false;
      })
      .addCase(bulkDeleteBookings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkDeleteBookings.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(bulkDeleteBookings.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getBookingById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.booking = null;
      })
      .addCase(getBookingById.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.booking = action.payload;
      })
      .addCase(getBookingById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchBusinessBookingCounts.pending, (state) => {
        state.businessCountsLoading = true;
        state.businessCountsError = null;
      })
      .addCase(fetchBusinessBookingCounts.fulfilled, (state, action) => {
        state.businessCountsLoading = false;
        state.businessCountsError = null;
        state.businessCounts = action.payload.businessCounts;
      })
      .addCase(fetchBusinessBookingCounts.rejected, (state, action) => {
        state.businessCountsLoading = false;
        state.businessCountsError = action.payload;
      });
  },
});

export const { resetCurrentBooking } = bookingSlice.actions;
export default bookingSlice.reducer;